import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";

export default function AutocompleteInput({
  name,
  placeholder,
  value,
  onChange,
  rpcName,
  minChars = 2,
  fillFields = {},
  valueKey,
  style = {},
  darkMode = null,
  multi = false,
  allowNew = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(multi ? "" : value || "");
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const requestRef = useRef(0);

  const isDark =
    darkMode !== null
      ? darkMode
      : document.body.classList.contains("dark-mode");

  useEffect(() => {
    if (!multi) setInputValue(value || "");
  }, [value, multi]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsFocused(false);
        setHoveredIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isFocused) return undefined;

    const term = String(inputValue || "").trim();
    if (term.length < minChars) {
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      const { data, error } = await supabase.rpc(rpcName, { search: term });

      if (requestId !== requestRef.current) return;

      if (error) {
        console.error(`Autocomplete error for ${name}:`, error);
        setSuggestions([]);
      } else {
        const results = Array.isArray(data) ? data : [];
        const next = [...results];

        // Generic "allowNew" is UI-only. Persistence belongs to the save RPC.
        if (allowNew) {
          const displayKey = valueKey || name || "name";
          next.push({
            id: "__new__",
            [displayKey]: `Add "${term}"`,
            __newValue: term,
          });
        }

        setSuggestions(next);
      }

      setHoveredIndex(-1);
      setLoading(false);
    }, 220);

    return () => clearTimeout(timer);
  }, [inputValue, isFocused, minChars, rpcName, name, allowNew, valueKey]);

  const emitChange = (fieldName, fieldValue) => {
    onChange?.({ target: { name: fieldName, value: fieldValue } });
  };

  const handleSelect = (suggestion) => {
    const mainValue =
      suggestion?.id === "__new__"
        ? suggestion.__newValue
        : valueKey
          ? suggestion?.[valueKey]
          : suggestion?.[name];

    if (mainValue == null) return;

    if (multi) {
      const current = Array.isArray(value) ? value : [];
      const exists = current.some((entry) => {
        const entryValue =
          typeof entry === "string"
            ? entry
            : entry?.id ?? entry?.name ?? entry?.value;
        return String(entryValue) === String(suggestion?.id ?? mainValue);
      });

      if (!exists) {
        emitChange(name, [...current, mainValue]);
      }
      setInputValue("");
    } else {
      emitChange(name, mainValue);
      setInputValue(String(mainValue));
    }

    if (suggestion?.id !== "__new__") {
      Object.entries(fillFields).forEach(([field, sourceKey]) => {
        emitChange(field, suggestion?.[sourceKey]);
      });
    }

    setSuggestions([]);
    setHoveredIndex(-1);
    setIsFocused(false);
  };

  const handleKeyDown = (event) => {
    if (!isFocused) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!suggestions.length) return;
      setHoveredIndex((current) =>
        current < suggestions.length - 1 ? current + 1 : 0
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!suggestions.length) return;
      setHoveredIndex((current) =>
        current > 0 ? current - 1 : suggestions.length - 1
      );
    } else if (event.key === "Enter" && hoveredIndex >= 0) {
      event.preventDefault();
      handleSelect(suggestions[hoveredIndex]);
    } else if (event.key === "Escape") {
      setSuggestions([]);
      setHoveredIndex(-1);
      setIsFocused(false);
    }
  };

  const dropdownVisible =
    isFocused &&
    (loading ||
      suggestions.length > 0 ||
      (String(inputValue || "").trim().length > 0 &&
        String(inputValue || "").trim().length < minChars));

  return (
    <div ref={containerRef} style={{ position: "relative", flex: 1 }}>
      <input
        name={name}
        placeholder={placeholder}
        value={inputValue}
        onChange={(event) => {
          const next = event.target.value;
          setInputValue(next);
          if (!multi) emitChange(name, next);
        }}
        onFocus={() => setIsFocused(true)}
        onKeyDown={handleKeyDown}
        style={{
          ...style,
          backgroundColor: isDark ? "#1e1e1e" : "#fff",
          color: isDark ? "#f1f1f1" : "#000",
          border: `1px solid ${isDark ? "#555" : "#ccc"}`,
          padding: "7px 9px",
          width: "100%",
          boxSizing: "border-box",
        }}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={dropdownVisible}
      />

      {dropdownVisible && (
        <ul
          style={{
            position: "absolute",
            top: "calc(100% + 3px)",
            left: 0,
            right: 0,
            background: isDark ? "#2c2f36" : "#fff",
            color: isDark ? "#f1f1f1" : "#000",
            border: `1px solid ${isDark ? "#555" : "#ccc"}`,
            borderRadius: "6px",
            maxHeight: "220px",
            overflowY: "auto",
            zIndex: 1000,
            margin: 0,
            padding: "4px 0",
            listStyle: "none",
            boxShadow: "0 6px 18px rgba(0,0,0,.16)",
          }}
        >
          {loading ? (
            <li style={{ padding: "8px 10px", opacity: 0.7 }}>Searching…</li>
          ) : String(inputValue || "").trim().length < minChars ? (
            <li style={{ padding: "8px 10px", opacity: 0.7 }}>
              Type at least {minChars} characters
            </li>
          ) : (
            suggestions.map((suggestion, index) => {
              const displayValue =
                suggestion?.id === "__new__"
                  ? suggestion?.[valueKey || name || "name"]
                  : valueKey
                    ? suggestion?.[valueKey]
                    : suggestion?.[name];

              return (
                <li
                  key={suggestion?.id || `${displayValue}-${index}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    handleSelect(suggestion);
                  }}
                  onMouseEnter={() => setHoveredIndex(index)}
                  style={{
                    padding: "8px 10px",
                    cursor: "pointer",
                    backgroundColor:
                      hoveredIndex === index
                        ? isDark
                          ? "#444"
                          : "#eee"
                        : "transparent",
                  }}
                >
                  {displayValue}
                  {suggestion?.brand && valueKey !== "brand" && (
                    <span style={{ opacity: 0.7 }}> ({suggestion.brand})</span>
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}

      {multi && Array.isArray(value) && value.length > 0 && (
        <div
          style={{
            marginTop: "5px",
            display: "flex",
            flexWrap: "wrap",
            gap: "5px",
          }}
        >
          {value.map((entry) => {
            const displayName =
              typeof entry === "string"
                ? entry
                : entry?.name || entry?.value || "";
            const key =
              typeof entry === "string"
                ? entry
                : entry?.id || entry?.name || displayName;

            return (
              <span
                key={key}
                style={{
                  background: isDark ? "#444" : "#ddd",
                  color: isDark ? "#f1f1f1" : "#000",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {displayName}
                <button
                  type="button"
                  onClick={() => {
                    const next = value.filter((candidate) => {
                      const candidateKey =
                        typeof candidate === "string"
                          ? candidate
                          : candidate?.id || candidate?.name;
                      return String(candidateKey) !== String(key);
                    });
                    emitChange(name, next);
                  }}
                  style={{
                    marginLeft: "4px",
                    cursor: "pointer",
                    background: "transparent",
                    border: "none",
                    color: "inherit",
                    fontWeight: "bold",
                  }}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
